import Phaser from 'phaser';
import { CHAPTERS } from '@/data';
import type { Chapter, Level } from '@/types';

// Progress storage key
const PROGRESS_KEY = 'cells_and_life_progress';

interface GameProgress {
  completedLevels: string[];
  completedChapters: string[];
}

function loadProgress(): GameProgress {
  try {
    const saved = localStorage.getItem(PROGRESS_KEY);
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (e) {
    console.warn('Failed to load progress:', e);
  }
  return { completedLevels: [], completedChapters: [] };
}

function saveProgress(progress: GameProgress): void {
  try {
    localStorage.setItem(PROGRESS_KEY, JSON.stringify(progress));
  } catch (e) {
    console.warn('Failed to save progress:', e);
  }
}

export function markLevelComplete(levelId: string): void {
  const progress = loadProgress();
  if (!progress.completedLevels.includes(levelId)) {
    progress.completedLevels.push(levelId);

    // Check if chapter is now complete
    for (const chapter of CHAPTERS) {
      const chapterLevelIds = chapter.levels.map(l => l.id);
      const allComplete = chapterLevelIds.every(id => progress.completedLevels.includes(id));
      if (allComplete && !progress.completedChapters.includes(chapter.id)) {
        progress.completedChapters.push(chapter.id);
      }
    }

    saveProgress(progress);
  }
}

export function isLevelUnlocked(levelId: string): boolean {
  const progress = loadProgress();

  // Find the level and its chapter
  for (let chapterIndex = 0; chapterIndex < CHAPTERS.length; chapterIndex++) {
    const chapter = CHAPTERS[chapterIndex];
    const levelIndex = chapter.levels.findIndex(l => l.id === levelId);

    if (levelIndex !== -1) {
      // First level of first chapter is always unlocked
      if (chapterIndex === 0 && levelIndex === 0) {
        return true;
      }

      // For other levels in chapter 1, check if previous level is complete
      if (chapterIndex === 0) {
        const prevLevelId = chapter.levels[levelIndex - 1].id;
        return progress.completedLevels.includes(prevLevelId);
      }

      // For other chapters, check if previous chapter is complete
      const prevChapter = CHAPTERS[chapterIndex - 1];
      if (!progress.completedChapters.includes(prevChapter.id)) {
        return false;
      }

      // First level of chapter is unlocked if chapter is unlocked
      if (levelIndex === 0) {
        return true;
      }

      // Check if previous level in this chapter is complete
      const prevLevelId = chapter.levels[levelIndex - 1].id;
      return progress.completedLevels.includes(prevLevelId);
    }
  }

  return false;
}

export function isLevelComplete(levelId: string): boolean {
  const progress = loadProgress();
  return progress.completedLevels.includes(levelId);
}

// Color palette - deep bioluminescent
const COLORS = {
  deepOcean: 0x040d1a,
  darkBlue: 0x0a1628,
  midBlue: 0x0f2847,
  accentCyan: 0x00d4ff,
  accentTeal: 0x00ffd4,
  accentGreen: 0x00ff88,
  accentPurple: 0xb366ff,
  accentGold: 0xffd700,
  textPrimary: 0xe8f4f8,
  textSecondary: 0x8ba4b4,
  textMuted: 0x4a6070,
};

export class MenuScene extends Phaser.Scene {
  private titleText!: Phaser.GameObjects.Text;
  private subtitleText!: Phaser.GameObjects.Text;
  private levelSelectOverlay?: Phaser.GameObjects.Container;
  private selectedChapterIndex = 0;
  private chapterIndicators: Phaser.GameObjects.Container[] = [];
  private levelCardsContainer?: Phaser.GameObjects.Container;

  constructor() {
    super({ key: 'MenuScene' });
  }

  create(): void {
    this.createBackground();
    this.createTitle();
    this.createMenuButtons();
    this.createFloatingOrbs();
  }

  private createBackground(): void {
    const { width, height } = this.cameras.main;

    // Deep gradient background
    const bg = this.add.graphics();

    // Base layer
    bg.fillStyle(COLORS.deepOcean, 1);
    bg.fillRect(0, 0, width, height);

    // Gradient layers
    for (let i = 0; i < 30; i++) {
      const ratio = i / 30;
      const y = ratio * height;
      const alpha = 0.02 + Math.sin(ratio * Math.PI) * 0.03;
      bg.fillStyle(COLORS.midBlue, alpha);
      bg.fillRect(0, y, width, height / 30 + 1);
    }

    // Subtle radial glow from center
    for (let i = 15; i >= 0; i--) {
      const radius = (width * 0.4) * (i / 15);
      const alpha = 0.015 * (1 - i / 15);
      bg.fillStyle(COLORS.accentCyan, alpha);
      bg.fillCircle(width / 2, height * 0.4, radius);
    }
  }

  private createTitle(): void {
    const centerX = this.cameras.main.width / 2;

    // Main title with distinctive styling
    this.titleText = this.add.text(centerX, 140, 'CELLS & LIFE', {
      fontFamily: '"Courier New", monospace',
      fontSize: '72px',
      fontStyle: 'bold',
      color: '#00d4ff',
      padding: { left: 30, right: 30, top: 15, bottom: 15 },
    });
    this.titleText.setOrigin(0.5);
    this.titleText.setStroke('#001a2e', 6);
    this.titleText.setShadow(0, 0, '#00d4ff', 25, true, true);

    // Subtitle with elegant italic
    this.subtitleText = this.add.text(centerX, 215, '~ The Living Code ~', {
      fontFamily: 'Georgia, serif',
      fontSize: '26px',
      fontStyle: 'italic',
      color: '#00ffd4',
    });
    this.subtitleText.setOrigin(0.5);
    this.subtitleText.setShadow(0, 0, '#00ffd4', 8, true, true);

    // Gentle floating animation
    this.tweens.add({
      targets: [this.titleText, this.subtitleText],
      y: '+=8',
      duration: 3000,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  private createMenuButtons(): void {
    const centerX = this.cameras.main.width / 2;
    const startY = 320;
    const spacing = 65;

    const buttons = [
      { text: 'PLAY', callback: () => this.startGame() },
      { text: 'PROGRESS', callback: () => this.showProgress() },
      { text: 'CELL DEMO', callback: () => this.startCellDemo() },
      { text: 'SETTINGS', callback: () => this.showSettings() },
      { text: 'ABOUT', callback: () => this.showAbout() },
    ];

    buttons.forEach((btn, i) => {
      const button = this.createGlowButton(centerX, startY + i * spacing, btn.text, btn.callback);

      // Staggered entrance animation
      button.setAlpha(0);
      button.setScale(0.8);
      this.tweens.add({
        targets: button,
        alpha: 1,
        scale: 1,
        duration: 400,
        delay: 200 + i * 100,
        ease: 'Back.easeOut',
      });
    });
  }

  private createGlowButton(x: number, y: number, text: string, callback: () => void): Phaser.GameObjects.Container {
    const container = this.add.container(x, y);
    const btnWidth = 220;
    const btnHeight = 48;

    // Outer glow layer
    const glow = this.add.graphics();
    glow.fillStyle(COLORS.accentCyan, 0.1);
    glow.fillRoundedRect(-btnWidth / 2 - 4, -btnHeight / 2 - 4, btnWidth + 8, btnHeight + 8, 14);

    // Main button background
    const bg = this.add.graphics();
    this.drawButtonBg(bg, btnWidth, btnHeight, false);

    // Button text
    const buttonText = this.add.text(0, 0, text, {
      fontFamily: '"Courier New", monospace',
      fontSize: '18px',
      fontStyle: 'bold',
      color: '#e8f4f8',
      letterSpacing: 3,
    });
    buttonText.setOrigin(0.5);

    container.add([glow, bg, buttonText]);
    container.setSize(btnWidth, btnHeight);
    container.setInteractive({ useHandCursor: true });

    // Hover effects
    container.on('pointerover', () => {
      this.drawButtonBg(bg, btnWidth, btnHeight, true);
      buttonText.setColor('#00ffd4');
      glow.clear();
      glow.fillStyle(COLORS.accentTeal, 0.2);
      glow.fillRoundedRect(-btnWidth / 2 - 6, -btnHeight / 2 - 6, btnWidth + 12, btnHeight + 12, 16);

      this.tweens.add({
        targets: container,
        scale: 1.03,
        duration: 150,
        ease: 'Quad.easeOut',
      });
    });

    container.on('pointerout', () => {
      this.drawButtonBg(bg, btnWidth, btnHeight, false);
      buttonText.setColor('#e8f4f8');
      glow.clear();
      glow.fillStyle(COLORS.accentCyan, 0.1);
      glow.fillRoundedRect(-btnWidth / 2 - 4, -btnHeight / 2 - 4, btnWidth + 8, btnHeight + 8, 14);

      this.tweens.add({
        targets: container,
        scale: 1,
        duration: 150,
        ease: 'Quad.easeOut',
      });
    });

    container.on('pointerdown', callback);

    return container;
  }

  private drawButtonBg(g: Phaser.GameObjects.Graphics, w: number, h: number, hover: boolean): void {
    g.clear();
    g.fillStyle(hover ? 0x0f3050 : COLORS.darkBlue, 0.9);
    g.fillRoundedRect(-w / 2, -h / 2, w, h, 10);
    g.lineStyle(2, hover ? COLORS.accentTeal : COLORS.accentCyan, hover ? 0.9 : 0.5);
    g.strokeRoundedRect(-w / 2, -h / 2, w, h, 10);
  }

  private createFloatingOrbs(): void {
    const { width, height } = this.cameras.main;

    // Create ethereal floating orbs
    for (let i = 0; i < 20; i++) {
      const x = Phaser.Math.Between(30, width - 30);
      const y = Phaser.Math.Between(30, height - 30);
      const size = Phaser.Math.Between(15, 50);
      const alpha = Phaser.Math.FloatBetween(0.05, 0.15);

      const orb = this.add.graphics();

      // Multi-layer soft glow
      for (let j = 4; j >= 0; j--) {
        const layerAlpha = alpha * (1 - j / 5) * 0.5;
        const layerSize = size + j * 8;
        orb.fillStyle(COLORS.accentCyan, layerAlpha);
        orb.fillCircle(0, 0, layerSize);
      }

      // Core
      orb.fillStyle(COLORS.accentCyan, alpha * 2);
      orb.fillCircle(0, 0, size * 0.3);

      orb.setPosition(x, y);
      orb.setDepth(-1);

      // Organic floating motion
      const duration = Phaser.Math.Between(4000, 8000);
      this.tweens.add({
        targets: orb,
        y: y + Phaser.Math.Between(-40, 40),
        x: x + Phaser.Math.Between(-25, 25),
        duration,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
        delay: Phaser.Math.Between(0, 2000),
      });

      // Subtle pulse
      this.tweens.add({
        targets: orb,
        alpha: alpha * 0.6,
        duration: Phaser.Math.Between(2000, 4000),
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    }
  }

  private startGame(): void {
    this.showLevelSelect();
  }

  private showLevelSelect(): void {
    if (this.levelSelectOverlay) return;

    const { width, height } = this.cameras.main;
    this.levelSelectOverlay = this.add.container(width / 2, height / 2);
    this.levelSelectOverlay.setDepth(100);

    // Animated dark overlay
    const overlay = this.add.graphics();
    overlay.fillStyle(0x000000, 0);
    overlay.fillRect(-width / 2, -height / 2, width, height);
    overlay.setInteractive(new Phaser.Geom.Rectangle(-width / 2, -height / 2, width, height), Phaser.Geom.Rectangle.Contains);
    this.levelSelectOverlay.add(overlay);

    // Animate overlay fade in
    this.tweens.add({
      targets: overlay,
      alpha: 0.88,
      duration: 300,
      onUpdate: () => {
        overlay.clear();
        overlay.fillStyle(0x000811, overlay.alpha);
        overlay.fillRect(-width / 2, -height / 2, width, height);
      },
    });

    // Panel dimensions - generous sizing for 5 chapters
    const panelWidth = 850;
    const panelHeight = 580;

    // Main panel with glass morphism effect
    const panelContainer = this.add.container(0, 0);
    panelContainer.setAlpha(0);
    panelContainer.setScale(0.9);

    // Panel glow backdrop
    const panelGlow = this.add.graphics();
    for (let i = 5; i >= 0; i--) {
      panelGlow.fillStyle(COLORS.accentCyan, 0.02 * (5 - i));
      panelGlow.fillRoundedRect(
        -panelWidth / 2 - i * 8,
        -panelHeight / 2 - i * 8,
        panelWidth + i * 16,
        panelHeight + i * 16,
        20 + i * 2
      );
    }

    // Main panel
    const panel = this.add.graphics();
    panel.fillStyle(0x061420, 0.97);
    panel.fillRoundedRect(-panelWidth / 2, -panelHeight / 2, panelWidth, panelHeight, 16);

    // Panel border with gradient effect
    panel.lineStyle(2, COLORS.accentCyan, 0.6);
    panel.strokeRoundedRect(-panelWidth / 2, -panelHeight / 2, panelWidth, panelHeight, 16);

    // Inner highlight line
    panel.lineStyle(1, COLORS.accentCyan, 0.15);
    panel.strokeRoundedRect(-panelWidth / 2 + 3, -panelHeight / 2 + 3, panelWidth - 6, panelHeight - 6, 14);

    // Title with glow
    const title = this.add.text(0, -panelHeight / 2 + 40, 'SELECT LEVEL', {
      fontFamily: '"Courier New", monospace',
      fontSize: '32px',
      fontStyle: 'bold',
      color: '#00d4ff',
      letterSpacing: 6,
    }).setOrigin(0.5);
    title.setShadow(0, 0, '#00d4ff', 12, true, true);

    // Decorative line under title
    const titleLine = this.add.graphics();
    titleLine.lineStyle(1, COLORS.accentCyan, 0.4);
    titleLine.lineBetween(-150, -panelHeight / 2 + 70, 150, -panelHeight / 2 + 70);

    // Close button - elegant X
    const closeBtn = this.createCloseButton(panelWidth / 2 - 35, -panelHeight / 2 + 35);

    panelContainer.add([panelGlow, panel, title, titleLine, closeBtn]);
    this.levelSelectOverlay.add(panelContainer);

    // Animate panel entrance
    this.tweens.add({
      targets: panelContainer,
      alpha: 1,
      scale: 1,
      duration: 350,
      ease: 'Back.easeOut',
    });

    // Create chapter indicators and level cards
    this.createChapterIndicators(panelWidth, panelHeight, panelContainer);
    this.showChapterLevels(CHAPTERS[this.selectedChapterIndex], panelWidth, panelHeight, panelContainer);
  }

  private createCloseButton(x: number, y: number): Phaser.GameObjects.Container {
    const container = this.add.container(x, y);

    const bg = this.add.graphics();
    bg.fillStyle(COLORS.darkBlue, 0.8);
    bg.fillCircle(0, 0, 18);
    bg.lineStyle(1, COLORS.accentCyan, 0.3);
    bg.strokeCircle(0, 0, 18);

    const closeX = this.add.text(0, 0, '✕', {
      fontFamily: 'Arial',
      fontSize: '20px',
      color: '#8ba4b4',
    }).setOrigin(0.5);

    container.add([bg, closeX]);
    container.setSize(36, 36);
    container.setInteractive({ useHandCursor: true });

    container.on('pointerover', () => {
      bg.clear();
      bg.fillStyle(0x1a3050, 1);
      bg.fillCircle(0, 0, 18);
      bg.lineStyle(2, COLORS.accentTeal, 0.8);
      bg.strokeCircle(0, 0, 18);
      closeX.setColor('#00ffd4');
    });

    container.on('pointerout', () => {
      bg.clear();
      bg.fillStyle(COLORS.darkBlue, 0.8);
      bg.fillCircle(0, 0, 18);
      bg.lineStyle(1, COLORS.accentCyan, 0.3);
      bg.strokeCircle(0, 0, 18);
      closeX.setColor('#8ba4b4');
    });

    container.on('pointerdown', () => this.closeLevelSelect());

    return container;
  }

  private createChapterIndicators(_panelWidth: number, panelHeight: number, parent: Phaser.GameObjects.Container): void {
    this.chapterIndicators = [];

    const indicatorY = -panelHeight / 2 + 110;
    const indicatorSpacing = 70;
    const totalWidth = (CHAPTERS.length - 1) * indicatorSpacing;
    const startX = -totalWidth / 2;

    // Connection line between indicators
    const connectionLine = this.add.graphics();
    connectionLine.lineStyle(2, COLORS.accentCyan, 0.15);
    connectionLine.lineBetween(startX, indicatorY, startX + totalWidth, indicatorY);
    parent.add(connectionLine);

    CHAPTERS.forEach((chapter, index) => {
      const x = startX + index * indicatorSpacing;
      const indicator = this.createChapterIndicator(x, indicatorY, index, chapter);
      parent.add(indicator);
      this.chapterIndicators.push(indicator);

      // Staggered entrance
      indicator.setAlpha(0);
      indicator.setScale(0);
      this.tweens.add({
        targets: indicator,
        alpha: 1,
        scale: 1,
        duration: 300,
        delay: 400 + index * 80,
        ease: 'Back.easeOut',
      });
    });

    this.updateChapterIndicators();
  }

  private createChapterIndicator(x: number, y: number, index: number, chapter: Chapter): Phaser.GameObjects.Container {
    const container = this.add.container(x, y);
    const size = 44;
    const isSelected = index === this.selectedChapterIndex;

    // Glow ring for selected
    const glow = this.add.graphics();
    container.add(glow);

    // Main circle
    const circle = this.add.graphics();
    container.add(circle);

    // Chapter number
    const numText = this.add.text(0, -2, `${index + 1}`, {
      fontFamily: '"Courier New", monospace',
      fontSize: '22px',
      fontStyle: 'bold',
      color: isSelected ? '#00ffd4' : '#8ba4b4',
    }).setOrigin(0.5);
    container.add(numText);

    // Chapter name label below
    const nameText = this.add.text(0, size / 2 + 14, this.getShortChapterName(chapter.title), {
      fontFamily: 'Georgia, serif',
      fontSize: '11px',
      color: '#6a8090',
    }).setOrigin(0.5);
    container.add(nameText);

    // Store references for updates
    container.setData('glow', glow);
    container.setData('circle', circle);
    container.setData('numText', numText);
    container.setData('nameText', nameText);
    container.setData('index', index);

    this.drawChapterIndicator(container, isSelected, false);

    container.setSize(size + 20, size + 40);
    container.setInteractive({ useHandCursor: true });

    container.on('pointerover', () => {
      if (index !== this.selectedChapterIndex) {
        this.drawChapterIndicator(container, false, true);
      }
    });

    container.on('pointerout', () => {
      if (index !== this.selectedChapterIndex) {
        this.drawChapterIndicator(container, false, false);
      }
    });

    container.on('pointerdown', () => {
      if (index !== this.selectedChapterIndex) {
        this.selectedChapterIndex = index;
        this.updateChapterIndicators();

        // Get panel container from parent
        const panelContainer = container.parentContainer;
        if (panelContainer) {
          this.showChapterLevels(chapter, 850, 580, panelContainer);
        }
      }
    });

    return container;
  }

  private getShortChapterName(title: string): string {
    // Shorten chapter names for display
    const shortNames: Record<string, string> = {
      'Discovering Cells': 'Discovery',
      'One or Many?': 'Uni/Multi',
      'What Makes Life?': 'Life Functions',
      'Cell Theory': 'Theory',
      'Building Life': 'Builder',
    };
    return shortNames[title] || title.slice(0, 10);
  }

  private drawChapterIndicator(container: Phaser.GameObjects.Container, selected: boolean, hover: boolean): void {
    const glow = container.getData('glow') as Phaser.GameObjects.Graphics;
    const circle = container.getData('circle') as Phaser.GameObjects.Graphics;
    const numText = container.getData('numText') as Phaser.GameObjects.Text;
    const nameText = container.getData('nameText') as Phaser.GameObjects.Text;

    const size = 44;

    glow.clear();
    circle.clear();

    if (selected) {
      // Glowing selected state
      for (let i = 4; i >= 0; i--) {
        glow.fillStyle(COLORS.accentTeal, 0.08 * (4 - i));
        glow.fillCircle(0, 0, size / 2 + 10 + i * 4);
      }

      circle.fillStyle(0x0a2535, 1);
      circle.fillCircle(0, 0, size / 2);
      circle.lineStyle(3, COLORS.accentTeal, 1);
      circle.strokeCircle(0, 0, size / 2);

      numText.setColor('#00ffd4');
      nameText.setColor('#00ffd4');
    } else if (hover) {
      glow.fillStyle(COLORS.accentCyan, 0.1);
      glow.fillCircle(0, 0, size / 2 + 6);

      circle.fillStyle(0x0a1e2e, 1);
      circle.fillCircle(0, 0, size / 2);
      circle.lineStyle(2, COLORS.accentCyan, 0.7);
      circle.strokeCircle(0, 0, size / 2);

      numText.setColor('#00d4ff');
      nameText.setColor('#8ba4b4');
    } else {
      circle.fillStyle(0x081828, 1);
      circle.fillCircle(0, 0, size / 2);
      circle.lineStyle(2, COLORS.accentCyan, 0.25);
      circle.strokeCircle(0, 0, size / 2);

      numText.setColor('#8ba4b4');
      nameText.setColor('#6a8090');
    }
  }

  private updateChapterIndicators(): void {
    this.chapterIndicators.forEach((indicator, i) => {
      const selected = i === this.selectedChapterIndex;
      this.drawChapterIndicator(indicator, selected, false);
    });
  }

  private showChapterLevels(chapter: Chapter, panelWidth: number, panelHeight: number, parent: Phaser.GameObjects.Container): void {
    // Remove existing level cards
    if (this.levelCardsContainer) {
      this.levelCardsContainer.destroy();
    }

    this.levelCardsContainer = this.add.container(0, 0);
    parent.add(this.levelCardsContainer);

    // Chapter title with elegant styling
    const chapterTitle = this.add.text(0, -panelHeight / 2 + 175, chapter.title, {
      fontFamily: 'Georgia, serif',
      fontSize: '24px',
      fontStyle: 'italic',
      color: '#00ffd4',
    }).setOrigin(0.5);
    chapterTitle.setShadow(0, 0, '#00ffd4', 6, true, true);
    this.levelCardsContainer.add(chapterTitle);

    // Scrollable area for levels
    const scrollAreaY = -panelHeight / 2 + 240;
    const cardWidth = panelWidth - 100;
    const cardHeight = 58;
    const cardSpacing = 8;

    // Create level cards with staggered animation
    chapter.levels.forEach((level, index) => {
      const y = scrollAreaY + index * (cardHeight + cardSpacing);
      const levelCard = this.createLevelCard(level, 0, y, cardWidth, cardHeight);
      this.levelCardsContainer!.add(levelCard);

      // Staggered entrance
      levelCard.setAlpha(0);
      levelCard.x = -30;
      this.tweens.add({
        targets: levelCard,
        alpha: 1,
        x: 0,
        duration: 300,
        delay: 100 + index * 60,
        ease: 'Quad.easeOut',
      });
    });

    // Boss challenge card
    if (chapter.bossChallenge) {
      const bossY = scrollAreaY + chapter.levels.length * (cardHeight + cardSpacing) + 10;
      const bossCard = this.createBossCard(chapter, 0, bossY, cardWidth, cardHeight);
      this.levelCardsContainer.add(bossCard);

      bossCard.setAlpha(0);
      bossCard.x = -30;
      this.tweens.add({
        targets: bossCard,
        alpha: 1,
        x: 0,
        duration: 300,
        delay: 100 + chapter.levels.length * 60 + 100,
        ease: 'Quad.easeOut',
      });
    }
  }

  private createLevelCard(level: Level, x: number, y: number, width: number, height: number): Phaser.GameObjects.Container {
    const container = this.add.container(x, y);
    const unlocked = isLevelUnlocked(level.id);
    const completed = isLevelComplete(level.id);

    // Card background
    const bg = this.add.graphics();
    this.drawLevelCardBg(bg, width, height, unlocked, completed, false);

    // Level number badge
    const badgeSize = 40;
    const badge = this.add.graphics();
    badge.fillStyle(unlocked ? 0x0a2535 : 0x0a1520, 1);
    badge.fillRoundedRect(-width / 2 + 15, -badgeSize / 2, badgeSize, badgeSize, 8);
    badge.lineStyle(2, unlocked ? (completed ? COLORS.accentGreen : COLORS.accentCyan) : 0x2a3a4a, unlocked ? 0.8 : 0.3);
    badge.strokeRoundedRect(-width / 2 + 15, -badgeSize / 2, badgeSize, badgeSize, 8);

    const levelNum = this.add.text(-width / 2 + 35, 0, level.id, {
      fontFamily: '"Courier New", monospace',
      fontSize: '16px',
      fontStyle: 'bold',
      color: unlocked ? (completed ? '#00ff88' : '#00d4ff') : '#3a4a5a',
    }).setOrigin(0.5);

    // Level name
    const levelName = this.add.text(-width / 2 + 75, -10, level.name, {
      fontFamily: 'Georgia, serif',
      fontSize: '18px',
      color: unlocked ? '#e8f4f8' : '#5a6a7a',
    }).setOrigin(0, 0.5);

    // Level type badge
    const typeColors: Record<string, number> = {
      discovery: COLORS.accentGreen,
      simulation: 0xffa500,
      classification: COLORS.accentCyan,
      builder: COLORS.accentPurple,
    };
    const typeColor = unlocked ? (typeColors[level.type] || COLORS.accentCyan) : 0x3a4a5a;

    const typeBadge = this.add.graphics();
    typeBadge.fillStyle(typeColor, 0.15);
    typeBadge.fillRoundedRect(-width / 2 + 75, 5, 90, 20, 4);

    const levelType = this.add.text(-width / 2 + 120, 15, level.type.toUpperCase(), {
      fontFamily: '"Courier New", monospace',
      fontSize: '11px',
      color: unlocked ? Phaser.Display.Color.IntegerToColor(typeColor).rgba : '#4a5a6a',
    }).setOrigin(0.5);

    // Status indicator
    const statusX = width / 2 - 40;
    let statusIcon: Phaser.GameObjects.Text | Phaser.GameObjects.Graphics;

    if (!unlocked) {
      // Lock icon (using graphics for consistent rendering)
      statusIcon = this.add.graphics();
      const lockColor = 0xffa500;
      // Lock body
      (statusIcon as Phaser.GameObjects.Graphics).fillStyle(lockColor, 0.9);
      (statusIcon as Phaser.GameObjects.Graphics).fillRoundedRect(statusX - 8, -4, 16, 14, 2);
      // Lock shackle
      (statusIcon as Phaser.GameObjects.Graphics).lineStyle(3, lockColor, 0.9);
      (statusIcon as Phaser.GameObjects.Graphics).beginPath();
      (statusIcon as Phaser.GameObjects.Graphics).arc(statusX, -6, 6, Math.PI, 0, false);
      (statusIcon as Phaser.GameObjects.Graphics).strokePath();
    } else if (completed) {
      // Checkmark with glow
      const checkGlow = this.add.graphics();
      checkGlow.fillStyle(COLORS.accentGreen, 0.2);
      checkGlow.fillCircle(statusX, 0, 18);
      container.add(checkGlow);

      statusIcon = this.add.text(statusX, 0, '✓', {
        fontFamily: 'Arial',
        fontSize: '24px',
        fontStyle: 'bold',
        color: '#00ff88',
      }).setOrigin(0.5);
    } else {
      // Play button
      statusIcon = this.add.graphics();
      (statusIcon as Phaser.GameObjects.Graphics).fillStyle(COLORS.accentTeal, 0.8);
      (statusIcon as Phaser.GameObjects.Graphics).fillTriangle(
        statusX - 8, -10,
        statusX - 8, 10,
        statusX + 10, 0
      );
    }

    container.add([bg, badge, levelNum, levelName, typeBadge, levelType, statusIcon]);
    container.setSize(width, height);

    if (unlocked) {
      container.setInteractive({ useHandCursor: true });

      container.on('pointerover', () => {
        this.drawLevelCardBg(bg, width, height, true, completed, true);
        this.tweens.add({
          targets: container,
          x: 5,
          duration: 150,
          ease: 'Quad.easeOut',
        });
      });

      container.on('pointerout', () => {
        this.drawLevelCardBg(bg, width, height, true, completed, false);
        this.tweens.add({
          targets: container,
          x: 0,
          duration: 150,
          ease: 'Quad.easeOut',
        });
      });

      container.on('pointerdown', () => this.launchLevel(level));
    }

    return container;
  }

  private drawLevelCardBg(g: Phaser.GameObjects.Graphics, w: number, h: number, unlocked: boolean, completed: boolean, hover: boolean): void {
    g.clear();

    if (!unlocked) {
      g.fillStyle(0x0a1520, 0.5);
      g.fillRoundedRect(-w / 2, -h / 2, w, h, 10);
      g.lineStyle(1, 0x2a3a4a, 0.3);
      g.strokeRoundedRect(-w / 2, -h / 2, w, h, 10);
    } else if (hover) {
      // Hover glow
      g.fillStyle(COLORS.accentTeal, 0.08);
      g.fillRoundedRect(-w / 2 - 4, -h / 2 - 4, w + 8, h + 8, 12);

      g.fillStyle(0x0a2535, 0.95);
      g.fillRoundedRect(-w / 2, -h / 2, w, h, 10);
      g.lineStyle(2, COLORS.accentTeal, 0.9);
      g.strokeRoundedRect(-w / 2, -h / 2, w, h, 10);
    } else {
      g.fillStyle(0x081828, 0.9);
      g.fillRoundedRect(-w / 2, -h / 2, w, h, 10);
      g.lineStyle(1, completed ? COLORS.accentGreen : COLORS.accentCyan, completed ? 0.5 : 0.3);
      g.strokeRoundedRect(-w / 2, -h / 2, w, h, 10);
    }
  }

  private createBossCard(chapter: Chapter, x: number, y: number, width: number, height: number): Phaser.GameObjects.Container {
    const container = this.add.container(x, y);

    // Special boss card styling
    const bg = this.add.graphics();

    // Outer glow
    bg.fillStyle(COLORS.accentGold, 0.08);
    bg.fillRoundedRect(-width / 2 - 4, -height / 2 - 4, width + 8, height + 8, 12);

    // Main background with gradient feel
    bg.fillStyle(0x1a1030, 0.95);
    bg.fillRoundedRect(-width / 2, -height / 2, width, height, 10);
    bg.lineStyle(2, COLORS.accentGold, 0.6);
    bg.strokeRoundedRect(-width / 2, -height / 2, width, height, 10);

    // Star icon
    const starIcon = this.add.text(-width / 2 + 35, 0, '⭐', {
      fontSize: '26px',
    }).setOrigin(0.5);

    // Boss name
    const bossName = this.add.text(-width / 2 + 70, -5, chapter.bossChallenge!.name, {
      fontFamily: 'Georgia, serif',
      fontSize: '18px',
      fontStyle: 'italic',
      color: '#ffd700',
    }).setOrigin(0, 0.5);

    // Boss label
    const bossLabel = this.add.text(-width / 2 + 70, 18, 'BOSS CHALLENGE', {
      fontFamily: '"Courier New", monospace',
      fontSize: '11px',
      color: '#b366ff',
      letterSpacing: 2,
    }).setOrigin(0, 0.5);

    // Crown/special indicator
    const crownIcon = this.add.text(width / 2 - 40, 0, '👑', {
      fontSize: '22px',
    }).setOrigin(0.5);

    container.add([bg, starIcon, bossName, bossLabel, crownIcon]);
    container.setSize(width, height);
    container.setInteractive({ useHandCursor: true });

    container.on('pointerover', () => {
      bg.clear();
      bg.fillStyle(COLORS.accentGold, 0.12);
      bg.fillRoundedRect(-width / 2 - 6, -height / 2 - 6, width + 12, height + 12, 14);
      bg.fillStyle(0x251845, 0.98);
      bg.fillRoundedRect(-width / 2, -height / 2, width, height, 10);
      bg.lineStyle(2, COLORS.accentGold, 0.9);
      bg.strokeRoundedRect(-width / 2, -height / 2, width, height, 10);

      this.tweens.add({
        targets: container,
        x: 5,
        duration: 150,
      });
    });

    container.on('pointerout', () => {
      bg.clear();
      bg.fillStyle(COLORS.accentGold, 0.08);
      bg.fillRoundedRect(-width / 2 - 4, -height / 2 - 4, width + 8, height + 8, 12);
      bg.fillStyle(0x1a1030, 0.95);
      bg.fillRoundedRect(-width / 2, -height / 2, width, height, 10);
      bg.lineStyle(2, COLORS.accentGold, 0.6);
      bg.strokeRoundedRect(-width / 2, -height / 2, width, height, 10);

      this.tweens.add({
        targets: container,
        x: 0,
        duration: 150,
      });
    });

    container.on('pointerdown', () => {
      // TODO: Launch boss challenge
      console.log('Boss challenge:', chapter.bossChallenge!.name);
    });

    return container;
  }

  private launchLevel(level: Level): void {
    this.closeLevelSelect();

    this.cameras.main.fadeOut(500, 4, 13, 26);

    this.cameras.main.once('camerafadeoutcomplete', () => {
      let sceneName = 'DiscoveryScene';
      if (level.type === 'simulation') {
        sceneName = 'SimulationScene';
      } else if (level.type === 'classification') {
        sceneName = 'ClassificationScene';
      } else if (level.type === 'builder') {
        sceneName = 'BuilderScene';
      }

      this.scene.start(sceneName, { levelId: level.id });
    });
  }

  private closeLevelSelect(): void {
    if (this.levelSelectOverlay) {
      // Animate out
      this.tweens.add({
        targets: this.levelSelectOverlay,
        alpha: 0,
        scale: 0.95,
        duration: 200,
        onComplete: () => {
          this.levelSelectOverlay?.destroy();
          this.levelSelectOverlay = undefined;
          this.levelCardsContainer = undefined;
          this.chapterIndicators = [];
          this.selectedChapterIndex = 0;
        },
      });
    }
  }

  private startCellDemo(): void {
    this.cameras.main.fadeOut(500, 4, 13, 26);
    this.cameras.main.once('camerafadeoutcomplete', () => {
      this.scene.start('CellDemoScene');
    });
  }

  private showProgress(): void {
    this.cameras.main.fadeOut(500, 4, 13, 26);
    this.cameras.main.once('camerafadeoutcomplete', () => {
      this.scene.start('ProgressDashboardScene');
    });
  }

  private showSettings(): void {
    this.cameras.main.fadeOut(500, 4, 13, 26);
    this.cameras.main.once('camerafadeoutcomplete', () => {
      this.scene.start('SettingsScene');
    });
  }

  private showAbout(): void {
    this.cameras.main.fadeOut(500, 4, 13, 26);
    this.cameras.main.once('camerafadeoutcomplete', () => {
      this.scene.start('AboutScene');
    });
  }
}
